'use client';

import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';
import { routeConfigs } from '@/app/api-docs/constants';

interface LayoutConfig {
  header?: boolean;
  footer?: boolean;
}

interface PageLayoutConfig {
  pc?: LayoutConfig;
  mobile?: LayoutConfig;
}

// 页面布局配置映射
const layoutConfigs: Record<string, PageLayoutConfig> = {
  '/watermark-removal/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/logo-remover/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/photo-stamp-remover/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/remove-signature/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/remove-text-from-image/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/sticker-remover/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/photo-restoration/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/image-repair/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/image-upscaler/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/unblur-photo/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/background-remover/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/png-maker/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/transparent-background-maker/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/black-background-maker/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/white-background-maker/processing': {
    mobile: { header: false, footer: false },
    pc: { footer: false },
  },
  '/ai-headshot-generator/select-images': {
    mobile: { footer: false, header: false },
    pc: { footer: false },
  },
  '/my-projects': {
    mobile: { footer: false },
    pc: { footer: false },
  },
  '/ai-expand/processing': {
    mobile: { footer: false, header: false },
    pc: { footer: false },
  },
  ...routeConfigs,
};

export interface DeviceContextType {
  hideHeader: boolean;
  hideFooter: boolean;
  deviceType: 'pc' | 'mobile';
}

const DeviceContext = createContext<DeviceContextType>({
  hideHeader: false,
  hideFooter: false,
  deviceType: 'pc',
});

const DeviceProvider = ({
  children,
  ssrDeviceType = 'pc',
}: {
  children: ReactNode;
  ssrDeviceType?: 'pc' | 'mobile';
}) => {
  const pathname = usePathname();
  // 1. 初始值用 ssrDeviceType
  const [deviceType, setDeviceType] = useState<'pc' | 'mobile'>(ssrDeviceType);

  // 2. 客户端检测，保证响应式
  useEffect(() => {
    // 判断屏幕宽度来判断设备类型
    const checkDeviceType = () => {
      const isMobile = window.innerWidth <= 768;
      setDeviceType(isMobile ? 'mobile' : 'pc');
    };
    checkDeviceType();
    const controller = new AbortController();
    const { signal } = controller;
    window.addEventListener('resize', checkDeviceType, { signal });
    return () => controller.abort();
  }, []);

  const matchedKey = Object.keys(layoutConfigs).find((key) =>
    pathname.includes(key)
  );

  // 获取当前页面的配置
  const currentConfig = matchedKey ? layoutConfigs[matchedKey] : null;
  const deviceConfig = currentConfig?.[deviceType as 'pc' | 'mobile'];

  // 如果没有配置，默认显示；如果有配置，根据配置决定是否显示
  const hideHeader = deviceConfig?.header === false;
  const hideFooter = deviceConfig?.footer === false;

  return (
    <DeviceContext.Provider value={{ hideHeader, hideFooter, deviceType }}>
      <>
        {!hideHeader && <Header />}
        {children}
        {!hideFooter && <Footer />}
      </>
    </DeviceContext.Provider>
  );
};

export function useDeviceContext() {
  return useContext(DeviceContext);
}

export default DeviceProvider;
